import { getItemByField, ObjectCreator } from "../../../../web-graphics";
import { debounce } from "../utils/debounce";

export class BaseVisualizatorProvider {
  constructor(settingsParser) {
    this.settingsParser = settingsParser;
    this.dataSourcesMap = new Map(); // key: data source id, value: dataSourceInfo
    /*
    dataSourceInfo: {
      channelIdMap: new Map(),      // key: channel name, value: channel id
      channels: new Map()           // key: channel id, value: channelInfo
    }
    channelInfo: {
      linkedChannels: [linked channel id],
      plots: [{ id: plot id }]
      currConfiguration: "config name"
      configurations: new Map(); // key: config name, value: configInfo
    }
    configInfo: {
      signalsIdMap: [],
      signals: new Map() // key: signal id, value: signalInfo
    }
    signalInfo: {
      plots: [plots id]
      clbK: 0
      clbB: 0
    }
    */
    this.eventHandlers = new Map(); // key: eventType, value: function(eventData)
    this.plotsInfo = [];

    this.parserCallbackPlaces = [
      "beforeSettingsParsing",
      "afterSettingsParsing",
      "afterSaveableSettingsApplied",
      "beforeWebglSettingsCreation",
      "afterWebglSettingsCreation",
      "beforeObjectsCreation",
      "afterCanvasObjectCreated",
      "afterGraphicItemCreated"
    ];
    this.parserCallbacks = new Map();
    for (let placeIndex = 0; placeIndex < this.parserCallbackPlaces.length; placeIndex++) {
      let place = this.parserCallbackPlaces[placeIndex];
      this.parserCallbacks.set(place, {
        forAll: [], // array of callbacks
        forType: new Map() // key: objectType, value: array of callbacks
      });
    }
  }

  init(settings, saveableUserSettings) {
    this.saveableUserSettings = saveableUserSettings === undefined ? undefined : ObjectCreator.deepCopy(saveableUserSettings);
    this.settings = ObjectCreator.deepCopy(settings);

    this.addParserCallback(
      "beforeObjectsCreation",
      null,
      function (parser) {
        let canvasCreator = parser.visualizatorCreator.canvasCreator;
        canvasCreator.addCallback(
          "beforeAddItems",
          function (canvasObject) {
            this.canvasObject = canvasObject;
          }.bind(this)
        );
      }.bind(this)
    );
  }

  create() {
    this._applyParserCallbacks("beforeSettingsParsing");
    this.settings = this.settingsParser.parseSettings(this.settings);

    for (let dataSourceIndex = 0; dataSourceIndex < this.settings.data.sources.length; dataSourceIndex++) {
      const dataSourceSettings = this.settings.data.sources[dataSourceIndex];
      const dataSourceMappedInfo = this._createDataSourceMappedInfo(dataSourceSettings);
      this.dataSourcesMap.set(dataSourceSettings.id, dataSourceMappedInfo);
    }

    this._applyParserCallbacks("afterSettingsParsing");
    this.settingsParser.applySaveableSettings(this.settings, this.saveableUserSettings);
    this._applyParserCallbacks("afterSaveableSettingsApplied");
    this.settingsParser.makeWebglConverters();
    this._applyParserCallbacks("beforeWebglSettingsCreation");
    this.webglSettings = this.settingsParser.createWebglSettings();
    this._applyParserCallbacks("afterWebglSettingsCreation");
    this.settingsParser.makeObjectCreators();
    this._applyParserCallbacks("beforeObjectsCreation");
    this.settingsParser.createObjects();
    this.updateSaveableSettings();
  }

  getPlotType(itemType) {
    if (!itemType.startsWith("plot")) {
      return undefined;
    }
    return this._getPlotSubtype(itemType);
  }

  getTypedPlotsInfo(type) {
    return this.plotsInfo
      .filter(info => this._hasFitPlotSubtype(info.type, type))
      .map(info => {
        return {
          id: info.id,
          viewSettings: info.viewSettings
        };
      });
  }

  getFirstPlot() {
    if (this.plotsInfo.length == 0) {
      return undefined;
    }
    return this.plotsInfo[0].webglPlot;
  }

  setCalibration(sourceId, channelName, signalsMap, clbK, clbB) {
    const dataSourceId = sourceId ?? 0;
    const dataSourceMappedInfo = this.dataSourcesMap.get(dataSourceId);
    if (dataSourceMappedInfo === undefined) {
      return;
    }
    const channelId = dataSourceMappedInfo.channelIdMap.get(channelName);
    if (channelId === undefined) {
      return;
    }
    const dataSourceSettings = getItemByField("id", dataSourceId, this.webglSettings.data.sources);
    const channelSettings = getItemByField("name", channelName, dataSourceSettings.channels);
    const channelMappedInfo = dataSourceMappedInfo.channels.get(channelId);

    for (let configIndex = 0; configIndex < channelSettings.configurations.length; configIndex++) {
      const configSettings = channelSettings.configurations[configIndex];
      const configMappedInfo = channelMappedInfo.configurations.get(configSettings.name);
      configMappedInfo.signalsIdMap = new Map();
      for (let signalIndex = 0; signalIndex < configSettings.signals.length; signalIndex++) {
        const signalSettings = configSettings.signals[signalIndex];
        const signalMappedInfo = configMappedInfo.signals.get(signalSettings.id);
        if (configSettings.signals.length > signalsMap.length) {
          signalMappedInfo.deviceSignalId = signalIndex;
          signalMappedInfo.clbK = clbK[0];
          signalMappedInfo.clbB = clbB[0];
          configMappedInfo.signalsIdMap.set(signalIndex, signalIndex);
        } else {
          const signalId = signalsMap[signalIndex];
          signalMappedInfo.deviceSignalId = signalId;
          signalMappedInfo.clbK = clbK[signalId];
          signalMappedInfo.clbB = clbB[signalId];
          configMappedInfo.signalsIdMap.set(signalId, signalIndex);
        }
      }
    }
  }

  setFrequency(sourceId, channelName, freq) {
    let dataSourceId = sourceId ?? 0;
    let channelId = this.getChannelId(dataSourceId, channelName);
    this.setChannelFrequency(dataSourceId, channelId, freq);
  }

  setChannelFrequency(sourceId, channelId, freq) {
    let dataSourceId = sourceId ?? 0;
    this.forEachChannelPlot(
      dataSourceId,
      channelId,
      function (plotInfo, channelSettings) {
        plotInfo.webglPlot.setFrequency(channelSettings.id_dataSource, channelSettings.id, freq);
        channelSettings.freq = freq;
      }.bind(this)
    );
  }

  setDefaultChannelsConfiguration(sourceId, channelName) {
    const dataSourceId = sourceId ?? 0;
    this.clearChannelsData(dataSourceId);

    const dataSourceSettings = getItemByField("id", dataSourceId, this.webglSettings.data.sources);
    const channelSettings = getItemByField("name", channelName, dataSourceSettings.channels);

    const dataSourceMappedInfo = this.dataSourcesMap.get(dataSourceId);
    const channelId = dataSourceMappedInfo.channelIdMap.get(channelName);
    const channelMappedInfo = dataSourceMappedInfo.channels.get(channelId);
    channelMappedInfo.currConfiguration = channelSettings.defaultConfiguration;

    this.forEachChannelPlot(
      dataSourceId,
      channelId,
      function (plotInfo, channelSettings) {
        plotInfo.setDefaultConfiguration(channelSettings.id_dataSource, channelSettings.id);
      }.bind(this)
    );
    this._updateLinkedChannelsConfiguration(dataSourceId, channelName, channelSettings.defaultConfiguration);
  }

  setChannelsConfiguration(sourceId, channelName, channelConfigurationName) {
    const dataSourceId = sourceId ?? 0;
    this.clearChannelsData(dataSourceId);

    const dataSourceMappedInfo = this.dataSourcesMap.get(dataSourceId);
    const channelId = dataSourceMappedInfo.channelIdMap.get(channelName);
    const channelMappedInfo = dataSourceMappedInfo.channels.get(channelId);
    channelMappedInfo.currConfiguration = channelConfigurationName;

    this.forEachChannelPlot(
      dataSourceId,
      channelId,
      function (plotInfo, channelSettings) {
        plotInfo.setConfiguration(channelSettings.id_dataSource, channelSettings.id, channelConfigurationName);
      }.bind(this)
    );
    this._updateLinkedChannelsConfiguration(dataSourceId, channelName, channelConfigurationName);
  }

  getChannelId(sourceId, channelName) {
    const dataSourceMappedInfo = this.dataSourcesMap.get(sourceId);
    return dataSourceMappedInfo.channelIdMap.get(channelName);
  }

  forEachChannelPlot(sourceId, channelId, action) {
    for (let plotIndex = 0; plotIndex < this.plotsInfo.length; plotIndex++) {
      this._forPlotChannel(sourceId, channelId, this.plotsInfo[plotIndex], action);
    }
  }

  addEventHandler(eventType, handler) {
    this.eventHandlers.set(eventType, handler);
  }

  addParserCallback(place, type, callback) {
    let placeCallbacks = this.parserCallbacks.get(place);
    if (placeCallbacks === undefined) {
      return;
    }
    if (type === undefined || type == null || type == "all") {
      placeCallbacks.forAll.push(callback);
    } else {
      let typedCallbacks = placeCallbacks.forType.get(type);
      if (typedCallbacks === undefined) {
        return;
      }
      typedCallbacks.push(callback);
    }
  }

  clearParserCallbacks() {
    this.parserCallbacks.clear();
    for (let placeIndex = 0; placeIndex < this.parserCallbackPlaces.length; placeIndex++) {
      const place = this.parserCallbackPlaces[placeIndex];
      this.parserCallbacks.set(place, {
        forAll: [], // array of callbacks
        forType: new Map() // key: objectType, value: array of callbacks
      });
    }
  }

  clearChannelsData(dataSourceId) {
    const sourceId = dataSourceId ?? 0;
    const dataSourceMappedInfo = this.dataSourcesMap.get(sourceId);
    if (dataSourceMappedInfo === undefined) {
      return;
    }
    dataSourceMappedInfo.channelIdMap.forEach(channelId => {
      const channelMappedInfo = dataSourceMappedInfo.channels.get(channelId);
      if (channelMappedInfo.plots.length == 0) {
        return;
      }
      for (let plotIndex = 0; plotIndex < channelMappedInfo.plots.length; plotIndex++) {
        const plotInfo = getItemByField("id", channelMappedInfo.plots[plotIndex].id, this.plotsInfo);
        plotInfo.webglPlot.clearChannelData(sourceId, channelId);
      }
    });
  }

  clearChannels(dataSourceId) {
    const sourceId = dataSourceId ?? 0;
    const dataSourceMappedInfo = this.dataSourcesMap.get(sourceId);
    if (dataSourceMappedInfo === undefined) {
      return;
    }
    dataSourceMappedInfo.channelIdMap.forEach(channelId => {
      const channelMappedInfo = dataSourceMappedInfo.channels.get(channelId);
      if (channelMappedInfo.plots.length == 0) {
        return;
      }
      for (let plotIndex = 0; plotIndex < channelMappedInfo.plots.length; plotIndex++) {
        const plotInfo = getItemByField("id", channelMappedInfo.plots[plotIndex].id, this.plotsInfo);
        plotInfo.webglPlot.clearChannelData(sourceId, channelId);
      }
    });
  }

  refreshGraphics() {
    requestAnimationFrame(this.canvasObject.draw.bind(this.canvasObject));
  }

  setScaleX(plotId, scaleXIndex, isNeedViewUpdate = false) {
    const plotInfo = getItemByField("id", plotId, this.plotsInfo);
    if (plotInfo === undefined) {
      return;
    }
    let currScaleX = plotInfo.viewSettings.scaleX.values[scaleXIndex];
    if (isNeedViewUpdate) {
      plotInfo.viewSettings.scaleX.current = currScaleX;
    }
    plotInfo.setScaleX(currScaleX.value);
    const signalsSettings = getItemByField("name", "signals", plotInfo.webglPlot.settings.plotContent?.central);
    if (signalsSettings === undefined) {
      return;
    }
    signalsSettings.settings.scaleX.currIndex = currScaleX.index;
    signalsSettings.settings.scaleX.currValue = currScaleX.value;
    this.updateSaveableSettings();
  }

  setScaleY(plotId, scaleYIndex, isNeedViewUpdate = false) {
    const plotInfo = getItemByField("id", plotId, this.plotsInfo);
    if (plotInfo === undefined) {
      return;
    }
    let currScaleY = plotInfo.viewSettings.scaleY.values[scaleYIndex];
    if (isNeedViewUpdate) {
      plotInfo.viewSettings.scaleY.current = currScaleY;
    }
    plotInfo.setScaleY(currScaleY.value);
    const signalsSettings = getItemByField("name", "signals", plotInfo.webglPlot.settings.plotContent?.central);
    if (signalsSettings === undefined) {
      return;
    }
    signalsSettings.settings.scaleY.currIndex = currScaleY.index;
    signalsSettings.settings.scaleY.currValue = currScaleY.value;
    this.updateSaveableSettings();
  }

  setFilters(plotId, newFiltersNames, isNeedViewUpdate = false) {
    const plotInfo = getItemByField("id", plotId, this.plotsInfo);
    if (plotInfo === undefined) {
      return;
    }
    if (isNeedViewUpdate) {
      plotInfo.viewSettings.filters.current = [...newFiltersNames];
    }
    plotInfo.setFilters(newFiltersNames);
    const signalsSettings = getItemByField("name", "signals", plotInfo.webglPlot.settings.plotContent?.central);
    if (signalsSettings === undefined) {
      return;
    }
    signalsSettings.settings.selectedFilters = [...newFiltersNames];
    this.updateSaveableSettings();
  }

  setChannelScaleX(plotId, scaleXIndex, isNeedViewUpdate = false) {
    const plotInfo = getItemByField("id", plotId, this.plotsInfo);
    if (plotInfo === undefined) {
      return;
    }
    const currScaleX = plotInfo.viewSettings.scaleX.values[scaleXIndex];
    if (isNeedViewUpdate) {
      plotInfo.viewSettings.scaleX.current = currScaleX;
    }
    plotInfo.setChannelScaleX(currScaleX.value, currScaleX.index);
    this.updateSaveableSettings();
  }

  setChannelScaleY(plotId, scaleYIndex, isNeedViewUpdate = false) {
    const plotInfo = getItemByField("id", plotId, this.plotsInfo);
    if (plotInfo === undefined) {
      return;
    }
    const currScaleY = plotInfo.viewSettings.scaleY.values[scaleYIndex];
    if (isNeedViewUpdate) {
      plotInfo.viewSettings.scaleY.current = currScaleY;
    }
    plotInfo.setChannelScaleY(currScaleY.value);
    this.updateSaveableSettings();
  }

  setChannelFilters(plotId, newFiltersNames, isNeedViewUpdate = false) {
    const plotInfo = getItemByField("id", plotId, this.plotsInfo);
    if (plotInfo === undefined) {
      return;
    }
    if (isNeedViewUpdate) {
      plotInfo.viewSettings.filters.current = [...newFiltersNames];
    }
    plotInfo.setChannelFilters(newFiltersNames);
    this.updateSaveableSettings();
  }

  setChannelUnit(sourceId, channelName, unit) {
    let dataSourceId = sourceId ?? 0;
    let channelId = this.getChannelId(dataSourceId, channelName);
    this.forEachChannelPlot(
      dataSourceId,
      channelId,
      function (plotInfo, channelSettings) {
        plotInfo.setChannelUnit(channelSettings.id_dataSource, channelSettings.id, unit);
      }.bind(this)
    );
    const dataSourceSettings = getItemByField("id", dataSourceId, this.settings.data.sources);
    const channelSettings = getItemByField("name", channelName, dataSourceSettings.channels);
    if (channelSettings === undefined) {
      return;
    }
    let dataSourceMappedInfo = this.dataSourcesMap.get(dataSourceId);
    if (dataSourceMappedInfo === undefined) {
      return;
    }
    this._forEachLinkedChannel(dataSourceMappedInfo, channelSettings.id, linkedChannelId => {
      this.forEachChannelPlot(
        dataSourceId,
        linkedChannelId,
        function (plotInfo, channelSettings) {
          plotInfo.setChannelUnit(channelSettings.id_dataSource, channelSettings.id, unit);
        }.bind(this)
      );
    });
  }

  initResizeFunc(canvas) {
    this.resizeCanvasFunc = () => {
      this.resizeTimeout = undefined;
      if (!canvas) {
        return;
      }
      const sizeRect = canvas.getBoundingClientRect();
      let parentWidth = Math.floor(sizeRect.width);
      let parentHeight = Math.floor(sizeRect.height);

      if (parentWidth == 0 || parentHeight == 0) {
        return;
      }
      if (this.canvasObject.settings.size.width.isNeedUpdate(parentWidth) || this.canvasObject.settings.size.height.isNeedUpdate(parentHeight)) {
        this.settings.view.canvas._parentWidth = parentWidth;
        this.settings.view.canvas._parentHeight = parentHeight;

        this.canvasObject.updateParentSize(parentWidth, parentHeight);

        this.resizeTimeout = setTimeout(this.resizeCanvasFunc, 200); // always check if resize was finished
        this.refreshGraphics();
      }
    };

    this.debounceFunc = debounce(this.resizeCanvasFunc, 400);
    window.addEventListener("resize", this.debounceFunc, true);
    this.initTimeout = setTimeout(
      function () {
        this.initTimeout = undefined;
        this.resizeCanvasFunc();
      }.bind(this),
      200
    ); // if page has scroll, scroll size can not be calculated at initialize moment
    this.refreshGraphics();
  }

  clear() {
    clearTimeout(this.resizeTimeout);
    clearTimeout(this.initTimeout);
    window.removeEventListener("resize", this.debounceFunc, true);
  }

  setActiveMode(isActive) {
    if (isActive) {
      this.canvasObject.settings.drawEnabled = true;
      this.initResizeFunc(this.canvasObject.settings.htmlObject.ref);
    } else {
      this.canvasObject.settings.drawEnabled = false;
      this.clear();
    }
  }

  updateSaveableSettings() {
    this.saveableUserSettings = this.settingsParser.createSaveableSettings(this.webglSettings);
    this.onSaveableUserSettingsChanged?.(this.saveableUserSettings);
  }

  setCanvasParentHtmlObject(parentHtmlObject, parentHtmlSizeObject) {
    if (parentHtmlSizeObject === undefined) {
      parentHtmlSizeObject = parentHtmlObject;
    }
    const sizeRect = parentHtmlSizeObject.getBoundingClientRect();
    const canvas = this.settings.view.canvas;
    canvas._parentWidth = Math.floor(sizeRect.width);
    canvas._parentHeight = Math.floor(sizeRect.height);
    canvas._parentHtmlObject = parentHtmlObject;
  }

  _forPlotChannel(sourceId, channelId, plotInfo, action) {
    let dataSourceSettings = getItemByField("id", sourceId, plotInfo.webglPlot.settings.dataSources);
    if (dataSourceSettings === undefined) {
      return;
    }
    let channelSettings = getItemByField("id", channelId, dataSourceSettings.channels);
    if (channelSettings !== undefined) {
      action(plotInfo, channelSettings);
    }
  }

  _updateLinkedChannelsConfiguration(sourceId, channelName, configName) {
    const dataSourceMappedInfo = this.dataSourcesMap.get(sourceId);
    const channelId = dataSourceMappedInfo.channelIdMap.get(channelName);
    if (channelId === undefined) {
      return;
    }
    this._forEachLinkedChannel(dataSourceMappedInfo, channelId, linkedChannelId => {
      const linkedChannelMappedInfo = dataSourceMappedInfo.channels.get(linkedChannelId);
      if (linkedChannelMappedInfo.plots.length == 0) {
        return;
      }
      linkedChannelMappedInfo.plots.forEach(channelPlotInfo => {
        let plotInfo = getItemByField("id", channelPlotInfo.id, this.plotsInfo);
        plotInfo.updateSourceConfiguration?.(configName);
        plotInfo.updateChannelSource?.(sourceId, linkedChannelId);
        plotInfo.webglPlot.onSignalLabelClicked(plotInfo.viewSettings.linkedChannel.id_dataSource, plotInfo.viewSettings.linkedChannel.id);
      });
    });
  }

  _forEachLinkedChannel(dataSourceMappedInfo, channelId, action) {
    const channelMappedInfo = dataSourceMappedInfo.channels.get(channelId);
    if (channelMappedInfo.linkedChannels.length == 0) {
      return;
    }
    channelMappedInfo.linkedChannels.forEach(action);
  }

  _applyParserCallbacks(place) {
    let placeCallbacks = this.parserCallbacks.get(place);
    if (placeCallbacks === undefined) {
      return;
    }
    placeCallbacks.forAll.forEach(callback => callback(this.settingsParser));
    placeCallbacks.forType.forEach((callbackArray, type) => callbackArray.forEach(callback => callback(this.settingsParser, type)));
  }

  _addPointsToWebglPlot(channelDataInfo, channelMappedInfo, sourceId, channelId) {
    const configMappedInfo = channelMappedInfo.configurations.get(channelMappedInfo.currConfiguration);
    let channelPlotIdList = [];
    for (let signalIndex = 0; signalIndex < channelDataInfo.signals.length; signalIndex++) {
      const signalMappedInfo = configMappedInfo.signals.get(signalIndex);
      if (signalMappedInfo !== undefined && signalMappedInfo.plots.length > 0) {
        for (let plotIndex = 0; plotIndex < signalMappedInfo.plots.length; plotIndex++) {
          const plotId = signalMappedInfo.plots[plotIndex];
          if (!channelPlotIdList.includes(plotId)) {
            channelPlotIdList.push(plotId);
          }
          const plotInfo = getItemByField("id", plotId, this.plotsInfo);
          plotInfo.addSignalPoints(sourceId, channelId, signalIndex, channelDataInfo.signals[signalIndex].data);
        }
      }
    }

    for (let plotIndex = 0; plotIndex < channelPlotIdList.length; plotIndex++) {
      let plotInfo = getItemByField("id", channelPlotIdList[plotIndex], this.plotsInfo);
      plotInfo.afterAddPointsToChannels(channelDataInfo);
    }
  }

  _getPhysicValues(data, signalCount, blockSize, configMappedInfo) {
    let mappedSignals = [];
    for (let signalIndex = 0; signalIndex < signalCount; signalIndex++) {
      const convertedSignal = {
        id: configMappedInfo.signalsIdMap.get(signalIndex),
        data: []
      };
      if (convertedSignal.id !== undefined) {
        convertedSignal.data.length = blockSize;
        const signalMappedInfo = configMappedInfo.signals.get(convertedSignal.id);
        const lsb = signalMappedInfo.clbK * 0.001;
        for (let pointIndex = 0; pointIndex < blockSize; pointIndex++) {
          convertedSignal.data[pointIndex] = data[pointIndex * signalCount + signalIndex] * lsb + signalMappedInfo.clbB;
          //convertedSignal.data[pointIndex] = (signalInfo.data[pointIndex] - signalMappedInfo.clbB) * lsb;
        }
      }
      mappedSignals.push(convertedSignal);
    }
    return mappedSignals;
  }

  _addChannelPointsToPlots(sourceId, restartTime, messageChannelInfo, dataSourceMappedInfo) {
    let channelId = dataSourceMappedInfo.channelIdMap.get(messageChannelInfo.name);
    if (channelId === undefined) {
      return;
    }
    const channelMappedInfo = dataSourceMappedInfo.channels.get(channelId);
    const configMappedInfo = channelMappedInfo.configurations.get(channelMappedInfo.currConfiguration);

    const channelDataInfo = {
      id_source: sourceId,
      id_channel: channelId,
      blockSize: messageChannelInfo.blockSize,
      pointCounter: messageChannelInfo.pointCounter,
      signals: this._getPhysicValues(messageChannelInfo.data, messageChannelInfo.signalCount, messageChannelInfo.blockSize, configMappedInfo)
    };

    this._addPointsToWebglPlot(channelDataInfo, channelMappedInfo, sourceId, channelId);
    this._forEachLinkedChannel(dataSourceMappedInfo, channelId, linkedChannelId => {
      const linkedChannelMappedInfo = dataSourceMappedInfo.channels.get(linkedChannelId);
      if (linkedChannelMappedInfo.plots.length == 0) {
        return;
      }

      const linkedChannelDataInfo = {
        id_source: sourceId,
        id_channel: linkedChannelId,
        blockSize: channelDataInfo.blockSize,
        pointCounter: channelDataInfo.pointCounter,
        signals: channelDataInfo.signals
      };

      let channelPlotIdList = [];
      for (let plotIndex = 0; plotIndex < linkedChannelMappedInfo.plots.length; plotIndex++) {
        const plotId = linkedChannelMappedInfo.plots[plotIndex].id;
        if (!channelPlotIdList.includes(plotId)) {
          channelPlotIdList.push(plotId);
        }
        const plotInfo = getItemByField("id", linkedChannelMappedInfo.plots[plotIndex].id, this.plotsInfo);
        for (let signalIndex = 0; signalIndex < channelDataInfo.signals.length; signalIndex++) {
          plotInfo.addSignalPoints(sourceId, channelId, signalIndex, channelDataInfo.signals[signalIndex].data);
        }
      }
      for (let plotIndex = 0; plotIndex < channelPlotIdList.length; plotIndex++) {
        let plotInfo = getItemByField("id", channelPlotIdList[plotIndex], this.plotsInfo);
        plotInfo.afterAddPointsToChannels(linkedChannelDataInfo);
      }
    });
  }

  _createSignalPlotIdList(sourceId, channelId, configName, signalId, channelMappedInfo) {
    if (channelMappedInfo.plots.length == 0) {
      return [];
    }
    const plotIdList = [];
    for (let plotIndex = 0; plotIndex < channelMappedInfo.plots.length; plotIndex++) {
      const plotId = channelMappedInfo.plots[plotIndex].id;
      const plotInfo = getItemByField("id", plotId, this.plotsInfo);
      const channelSettings = plotInfo.webglPlot.getChannelSettings(sourceId, channelId);
      const configSettings = getItemByField("id", configName, channelSettings.configurations);
      if (configSettings === undefined) {
        continue;
      }
      const signalSettings = getItemByField("id", signalId, configSettings.signals);
      if (signalSettings !== undefined) {
        plotIdList.push(plotId);
      }
    }
    return plotIdList;
  }

  _addMarkToPlots(sourceId, markType, data) {
    const dataSourceMappedInfo = this.dataSourcesMap.get(sourceId);
    const markId = dataSourceMappedInfo.markIdMap.get(markType);
    if (markId === undefined) {
      return;
    }
    //console.log(`BaseVisualizatorProvider._addMarkToPlots ${markType} ${JSON.stringify(data, null, ' ')}`);
    const markMappedInfo = dataSourceMappedInfo.marks.get(markId);
    if (markMappedInfo !== undefined && markMappedInfo.plots.length > 0) {
      for (let plotIndex = 0; plotIndex < markMappedInfo.plots.length; plotIndex++) {
        const plotId = markMappedInfo.plots[plotIndex];
        const plotInfo = getItemByField("id", plotId, this.plotsInfo);
        plotInfo.addMark(sourceId, markId, data);
      }
    }
  }

  _addPointsToPlots(sourceId, message) {
    const dataSourceMappedInfo = this.dataSourcesMap.get(sourceId);
    for (let channelIndex = 0; channelIndex < message.channels.length; channelIndex++) {
      this._addChannelPointsToPlots(sourceId, message.time, message.channels[channelIndex], dataSourceMappedInfo);
    }
  }

  _addPlotIdToChannelMappedInfo(dataSourcesMappedInfo, channelSettings, plotId, channelPloInfoCreator) {
    const channelMappedInfo = dataSourcesMappedInfo.channels.get(channelSettings.id);
    if (channelMappedInfo === undefined) {
      return;
    }
    const mappedPlotId = getItemByField("id", plotId, channelMappedInfo.plots);
    if (mappedPlotId === undefined) {
      channelMappedInfo.plots.push(channelPloInfoCreator(plotId));
    }
    for (let configIndex = 0; configIndex < channelSettings.configurations.length; configIndex++) {
      const configSettings = channelSettings.configurations[configIndex];
      const configMappedInfo = channelMappedInfo.configurations.get(configSettings.name);
      for (let signalIndex = 0; signalIndex < configSettings.signals.length; signalIndex++) {
        const signalSettings = configSettings.signals[signalIndex];
        const signalMappedInfo = configMappedInfo.signals.get(signalSettings.id);
        if (!signalMappedInfo.plots.includes(plotId)) {
          signalMappedInfo.plots.push(plotId);
        }
      }
    }
  }

  _addPlotIdToMarkMappedInfo(dataSourcesMappedInfo, markSettings, plotId) {
    const markMappedInfo = dataSourcesMappedInfo.marks.get(markSettings.id_mark);
    if (markMappedInfo === undefined) {
      return;
    }
    const mappedPlotId = getItemByField("id", plotId, markMappedInfo.plots);
    if (mappedPlotId === undefined) {
      markMappedInfo.plots.push(plotId);
    }
  }

  _addLinkedChannelPointsToWebglPlot(channelDataInfo, dataSourceMappedInfo, sourceId, channelId) {
    const channelMappedInfo = dataSourceMappedInfo.channels.get(channelId);
    if (channelMappedInfo.plots.length == 0) {
      return;
    }
    for (let plotIndex = 0; plotIndex < channelMappedInfo.plots.length; plotIndex++) {
      let plotInfo = getItemByField("id", channelMappedInfo.plots[plotIndex].id, this.plotsInfo);
      plotInfo.addPoints(sourceId, channelId, channelDataInfo.signals);
      plotInfo.afterAddPointsToChannels(channelDataInfo);
    }
  }

  _addValueToMappedArray(arrayMap, key, newValue) {
    if (arrayMap.has(key)) {
      let mappedArray = arrayMap.get(key);
      mappedArray.push(newValue);
    } else {
      arrayMap.set(key, [newValue]);
    }
  }

  _createDataSourceMappedInfo(dataSourceSettings) {
    const dataSourceMappedInfo = {
      channelIdMap: new Map(), // key: channel name, value: channel id
      channels: new Map(), // key: channel id, value: channelInfo
      markIdMap: new Map(), // key: mark name, value: mark id
      marks: new Map() // key: mark id, value: markInfo
    };
    const linkedChannels = [];
    for (let channelIndex = 0; channelIndex < dataSourceSettings.channels.length; channelIndex++) {
      const channelSettings = dataSourceSettings.channels[channelIndex];
      const channelInfo = {
        linkedChannels: [], // channels id list
        plots: [], // plots id list
        currConfiguration: channelSettings.currConfiguration,
        configurations: new Map() // key: config name, value: configInfo
      };
      if (channelSettings.conversionSettings?.type == "rhythm") {
        linkedChannels.push({
          id: channelSettings.conversionSettings.id_sourceChannel,
          linkedId: channelSettings.id
        });
      }
      dataSourceMappedInfo.channelIdMap.set(channelSettings.name, channelSettings.id);
      dataSourceMappedInfo.channels.set(channelSettings.id, channelInfo);
      for (let configIndex = 0; configIndex < channelSettings.configurations.length; configIndex++) {
        const configSettings = channelSettings.configurations[configIndex];
        const configInfo = {
          signalsIdMap: new Map(), // key: device signal id, value: vis signal id
          signals: new Map() // key: vis signal id, value: signalInfo
        };
        channelInfo.configurations.set(configSettings.name, configInfo);
        for (let signalIndex = 0; signalIndex < configSettings.signals.length; signalIndex++) {
          const signalSettings = configSettings.signals[signalIndex];
          const signalInfo = {
            deviceSignalId: signalIndex,
            plots: [], // plots id list
            clbK: 0,
            clbB: 0
          };
          configInfo.signals.set(signalSettings.id, signalInfo);
        }
      }
    }
    for (let channelIndex = 0; channelIndex < linkedChannels.length; channelIndex++) {
      const linkedChannelInfo = linkedChannels[channelIndex];
      const channelInfo = dataSourceMappedInfo.channels.get(linkedChannelInfo.id);
      channelInfo.linkedChannels.push(linkedChannelInfo.linkedId);
    }
    if (dataSourceSettings.marks === undefined) {
      dataSourceSettings.marks = [];
    }
    for (let markIndex = 0; markIndex < dataSourceSettings.marks.length; markIndex++) {
      const markSettings = dataSourceSettings.marks[markIndex];
      const markInfo = {
        plots: [] // plots id list
      };
      dataSourceMappedInfo.markIdMap.set(markSettings.name, markSettings.id);
      dataSourceMappedInfo.marks.set(markSettings.id, markInfo);
    }
    return dataSourceMappedInfo;
  }

  _signalInfoHashCode(channelId, signalId) {
    return channelId * 1000 + signalId;
  }

  _getPlotSubtype(plotFullType) {
    if (plotFullType.length < 8) {
      return undefined;
    }
    const plotType = plotFullType.substring(5);
    const plotTypeParts = plotType.split(":");
    if (plotTypeParts.length != 2) {
      return undefined;
    }
    return {
      signalType: plotTypeParts[0],
      targetType: plotTypeParts[1]
    };
  }

  _hasFitPlotSubtype(plotFullType, subtype) {
    let plotSubtype = this._getPlotSubtype(plotFullType);
    if (plotSubtype === undefined) {
      return false;
    }
    return plotSubtype.targetType == subtype;
  }
}
